/**
 * Filter model for the Task List screen, shared by TaskListView (which owns the
 * state) and TaskListToolbar (which renders the active-filter badge).
 *
 * A filter entry is one of:
 *   { column, op: "in",       values: string[] }
 *   { column, op: "contains", value: string }
 *   { column, op: "between",  from?: string, to?: string }
 *
 * State is held as a Record<columnKey, ColumnFilter> and serialised whole on
 * every fetch -- the server keeps no filter state of its own, so a partial
 * payload silently drops filters rather than leaving them applied.
 */

import { fetchColumnValues } from "../service/Task";

export const MAX_COLUMN_FILTERS = 15;
export const MAX_WORKFLOW_IDS = 3;
// Deep pages are OFFSET-based server-side and get slower the further out you
// go, so the picker deliberately stops at 100 and offers no "All".
export const MAX_PAGE_SIZE = 100;
// Only the free-text inputs are debounced -- OK / Reset / paging are not.
export const TEXT_FILTER_DEBOUNCE_MS = 350;

// "" is a real, matchable value on the backend: it selects rows whose cell is
// blank. The picker surfaces it under this label.
export const BLANK_FILTER_VALUE = "";
export const BLANK_FILTER_LABEL = "(Blanks)";

export const PRIORITY_FILTER_VALUES = [
  "Low_Priority",
  "Medium_Priority",
  "High_Priority",
];

// The five statuses named in the BLF-2028 API contract.
export const TASK_STATUS_FILTER_VALUES = [
  "Active",
  "Assigned",
  "In_Progress",
  "Completed",
  "Finished",
];

// The list screen has always rendered badges for these two as well (see
// getStatusBadgeClass / taskStatusMeta in TaskListView), so they stay in the
// picker even though the contract's enum doesn't name them. Drop them here if
// the backend ever rejects them as invalid values (header.code 601).
export const EXTRA_TASK_STATUS_VALUES = ["On_Hold", "Pending"];

export const ALL_TASK_STATUS_FILTER_VALUES = [
  ...TASK_STATUS_FILTER_VALUES,
  ...EXTRA_TASK_STATUS_VALUES,
];

// Sent verbatim as `columnFilters` values, and the backend matches `source`
// case-sensitively on the stored form -- so these stay title-cased, not SCREAMING.
export const SOURCE_FILTER_VALUES = ["Automated", "Manual", "Imported", "Mobile"];

/**
 * The filter column is whatever key GET /list/columns reports. For most fixed
 * columns that is the same string the grid already uses as its header value
 * ("Account No.", "Area Code", "created_By", ...), so the mapping is identity
 * by default and only genuinely different spellings are listed here.
 *
 * Inventing translations (accountNo, areaCode, mobileNo, ...) named columns the
 * backend doesn't have, which is why those filters did nothing.
 *
 * This table is only the fallback for when the columns metadata hasn't loaded.
 * Prefer resolving against the metadata itself -- see columnKeyForHeader in
 * TaskListView, which looks the header up in the /list/columns response and uses
 * that entry's real key.
 */
const HEADER_KEY_TO_COLUMN_KEY = {
  // Response field is `taskStatus`; the filter column is `status`.
  taskStatus: "status",
  // Column key is `finishedDate`; the grid header is `completedDate`.
  completedDate: "finishedDate",
};

export const toColumnKey = (headerKey) => {
  const key = String(headerKey ?? "").trim();
  if (!key) return "";
  return HEADER_KEY_TO_COLUMN_KEY[key] || key;
};

/**
 * Case- and punctuation-insensitive form of a column key, used to decide whether
 * a grid header and a /list/columns entry are the same column. Aliases exist
 * where the two name the column differently ("status" vs "taskStatus",
 * "finishedDate" vs "completedDate").
 *
 * Module-level so it can be used both by the component's memoised wrapper and by
 * memos that run earlier in the render.
 */
export const canonicalColumnKey = (headerKey = "") => {
  const normalized = String(headerKey).toLowerCase().replace(/[^a-z0-9]/g, "");

  if (normalized === "category") return "category";
  if (normalized === "tarrif") return "tariff";
  if (normalized === "status") return "taskstatus";
  if (normalized === "meterno") return "meterno";
  if (normalized === "finisheddate") return "completeddate";

  return normalized;
};

export const isBlankOptionValue = (value) => String(value ?? "") === BLANK_FILTER_VALUE;

export const makeInFilter = (column, values) => ({
  column: toColumnKey(column),
  op: "in",
  values: Array.isArray(values) ? values.map((value) => String(value ?? "")) : [],
});

export const makeContainsFilter = (column, value) => ({
  column: toColumnKey(column),
  op: "contains",
  value: String(value ?? ""),
});

export const makeBetweenFilter = (column, from, to) => ({
  column: toColumnKey(column),
  op: "between",
  from: String(from ?? ""),
  to: String(to ?? ""),
});

/**
 * True when the entry would narrow the result set. An empty picker means
 * "don't filter this column" -- never "match nothing" -- so empty entries are
 * dropped rather than sent as an impossible predicate.
 */
export const isMeaningfulColumnFilter = (filter) => {
  if (!filter || !filter.column) return false;
  if (filter.op === "in") {
    return Array.isArray(filter.values) && filter.values.length > 0;
  }
  if (filter.op === "contains") {
    return String(filter.value ?? "").trim() !== "";
  }
  if (filter.op === "between") {
    return Boolean(String(filter.from ?? "").trim() || String(filter.to ?? "").trim());
  }
  return false;
};

/**
 * Record<columnKey, ColumnFilter> -> the `columnFilters` array the search
 * endpoint expects, with the drop rules above applied and the payload capped
 * at MAX_COLUMN_FILTERS (the backend rejects more with header.code 601).
 */
export const serializeColumnFilters = (filtersMap) => {
  const serialized = Object.values(filtersMap || {})
    .filter(isMeaningfulColumnFilter)
    .map((filter) => {
      // Re-map on the way out so the wire format is authoritative here: a
      // filter persisted by an older build (or stored under a display key)
      // can't leak the wrong column name into the request.
      const column = toColumnKey(filter.column);
      if (filter.op === "in") {
        return { column, op: "in", values: filter.values };
      }
      if (filter.op === "contains") {
        return { column, op: "contains", value: String(filter.value).trim() };
      }
      const between = { column, op: "between" };
      if (String(filter.from ?? "").trim()) between.from = String(filter.from).trim();
      if (String(filter.to ?? "").trim()) between.to = String(filter.to).trim();
      return between;
    });

  if (serialized.length > MAX_COLUMN_FILTERS) {
    console.warn(
      `Task list: ${serialized.length} column filters exceeds the server cap of ${MAX_COLUMN_FILTERS}; sending the first ${MAX_COLUMN_FILTERS}.`
    );
    return serialized.slice(0, MAX_COLUMN_FILTERS);
  }
  return serialized;
};

/** Badge count on the "Column Filters" toggle -- entries left after the drop rules. */
export const countActiveColumnFilters = (filtersMap) =>
  Object.values(filtersMap || {}).filter(isMeaningfulColumnFilter).length;

/**
 * Serialized `columnFilters` -> the `columnConditions` the column-values
 * endpoint narrows by: every *other* column's `in` selection. The column being
 * opened is excluded so its own selection doesn't hide its unchecked values.
 * `contains` / `between` have no equivalent in that contract and are skipped.
 */
export const toColumnConditions = (serializedFilters, excludeColumn) => {
  const excluded = toColumnKey(excludeColumn);
  return (serializedFilters || [])
    .filter(
      (filter) =>
        filter?.op === "in" &&
        Array.isArray(filter.values) &&
        filter.values.length > 0 &&
        toColumnKey(filter.column) !== excluded
    )
    .map((filter) => ({ column: toColumnKey(filter.column), value: filter.values }));
};

/**
 * Option list for a column's checkbox picker.
 *
 * priority / taskStatus / source are fixed enums and are answered locally with
 * no round trip. Everything else asks the server for distinct values across the
 * whole workflow -- never the loaded page, which holds only `size` rows out of
 * as many as ~400k.
 *
 * Resolves to [] when the column has no values or the endpoint isn't deployed
 * yet; the caller falls back to a `contains` box in that case so the column
 * stays filterable either way.
 */
export const getColumnValues = async (workflowIds, column, search, columnConditions) => {
  const columnKey = toColumnKey(column);
  const fixed =
    columnKey === "priority"
      ? PRIORITY_FILTER_VALUES
      : columnKey === "status"
        ? ALL_TASK_STATUS_FILTER_VALUES
        : columnKey === "source"
          ? SOURCE_FILTER_VALUES
          : null;

  if (fixed) {
    const query = String(search ?? "").trim().toLowerCase();
    if (!query) return fixed;
    return fixed.filter((value) => value.toLowerCase().includes(query));
  }

  const values = await fetchColumnValues({
    workflowIds,
    column: columnKey,
    search,
    columnConditions,
  });
  return values.filter((value) => String(value ?? "").trim() !== "").sort();
};

/** Columns answered locally, with no request needed. */
export const hasFixedOptionList = (column) =>
  ["priority", "status", "source"].includes(toColumnKey(column));
